import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "hooks/useAuth";
import { shouldRetryRequest } from "api/apiError";
import { clientKeys, fetchClientSummary } from "api/clients";
import {
  fetchEntitlements,
  ModuleCatalogEntry,
  teleopdKeys,
  updateEntitlements,
} from "api/teleopdLink";
import { useApiMutation } from "Routes/Billing/hooks";
import { EntitlementDraft, ModuleGroup } from "./types";

const EMPTY_CATALOG: ModuleCatalogEntry[] = [];

const groupCatalog = (catalog: ModuleCatalogEntry[]): ModuleGroup[] => {
  const groups: ModuleGroup[] = [];
  catalog.forEach((entry) => {
    const group = groups.find((candidate) => candidate.name === entry.group);
    if (group) {
      group.modules.push(entry);
    } else {
      groups.push({ name: entry.group, modules: [entry] });
    }
  });
  return groups;
};

/** Keeps catalog names only, in catalog order. */
const inCatalogOrder = (catalog: ModuleCatalogEntry[], names: string[]) =>
  catalog.map((entry) => entry.name).filter((name) => names.includes(name));

/** /clients/:tenantId/modules — which TeleOpd navbar modules SAP entitles for this client. */
const useClientModulesData = (tenantId: string) => {
  const { hasPermission } = useAuth();
  const canConnect = hasPermission("billing", "configure");

  const entitlementsQuery = useQuery({
    queryKey: teleopdKeys.entitlements(tenantId),
    queryFn: () => fetchEntitlements(tenantId),
    retry: shouldRetryRequest,
  });
  const clientQuery = useQuery({
    queryKey: clientKeys.detail(tenantId),
    queryFn: () => fetchClientSummary(tenantId),
    retry: shouldRetryRequest,
    staleTime: 5 * 60 * 1000,
  });

  const entitlements = entitlementsQuery.data;
  // Locked until the production release: view only, whatever the user's role (the API refuses too).
  const locked = Boolean(entitlements && entitlements.stage !== "beta");
  // Modules travel in TeleOpd's sync, so nothing here changes until its TeleOpd is connected.
  const unconnected = Boolean(entitlements && !entitlements.linked);
  const canEdit = !locked && !unconnected && hasPermission("tenantManagement", "write");
  const catalog = entitlements ? entitlements.catalog : EMPTY_CATALOG;
  const groups = React.useMemo(() => groupCatalog(catalog), [catalog]);

  // Saved state as a stable key so edits are only reset when the server copy changes.
  const savedKey = entitlements
    ? JSON.stringify({
        managed: entitlements.managed,
        modules: inCatalogOrder(entitlements.catalog, entitlements.modules),
      })
    : "";
  const saved = React.useMemo<EntitlementDraft | null>(
    () => (savedKey ? (JSON.parse(savedKey) as EntitlementDraft) : null),
    [savedKey]
  );
  const [draft, setDraft] = React.useState<EntitlementDraft>({ managed: false, modules: [] });

  React.useEffect(() => {
    if (saved) {
      setDraft(saved);
    }
  }, [saved]);

  const selected = inCatalogOrder(catalog, draft.modules);
  const dirty = Boolean(
    saved && (saved.managed !== draft.managed || saved.modules.join("|") !== selected.join("|"))
  );

  const localModules = entitlements ? entitlements.localModules : [];
  const importable = inCatalogOrder(catalog, localModules);
  const unknownLocal = localModules.filter((name) => !catalog.some((entry) => entry.name === name));

  const save = useApiMutation((input: EntitlementDraft) => updateEntitlements(tenantId, input), {
    success: (result) =>
      result.managed
        ? "Modules saved. TeleOpd applies them on its next sync."
        : "Modules saved. TeleOpd keeps its own list while “Managed by SAP” is off.",
    invalidate: [teleopdKeys.entitlements(tenantId), teleopdKeys.link(tenantId)],
  });

  const toggleModule = (name: string) =>
    setDraft((current) => ({
      ...current,
      modules: current.modules.includes(name)
        ? current.modules.filter((entry) => entry !== name)
        : [...current.modules, name],
    }));

  const setGroup = (group: ModuleGroup, checked: boolean) => {
    const names = group.modules.map((entry) => entry.name);
    setDraft((current) => ({
      ...current,
      modules: checked
        ? current.modules.concat(names.filter((name) => !current.modules.includes(name)))
        : current.modules.filter((name) => !names.includes(name)),
    }));
  };

  return {
    states: {
      canEdit,
      locked,
      unconnected,
      canConnect,
      entitlements,
      clientName: clientQuery.data ? clientQuery.data.name : null,
      clientSlug: clientQuery.data && clientQuery.data.slug ? clientQuery.data.slug : null,
      clientUri: clientQuery.data && clientQuery.data.uri ? clientQuery.data.uri : null,
      groups,
      catalog,
      draft,
      selected,
      dirty,
      localModules,
      importable,
      unknownLocal,
      saving: save.isLoading,
    },
    entitlementsQuery,
    setManaged: (managed: boolean) => setDraft((current) => ({ ...current, managed })),
    toggleModule,
    setGroup,
    importFromTeleopd: () => setDraft((current) => ({ ...current, modules: importable })),
    discard: () => {
      if (saved) {
        setDraft(saved);
      }
    },
    save: () => save.mutate({ managed: draft.managed, modules: selected }),
  };
};

export default useClientModulesData;
